import { useEffect, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/usePermissions";
import { MoreHorizontal, UserPlus, Copy, Search, Mail, UserCog } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import CreateTeamMemberDialog from "@/components/admin/CreateTeamMemberDialog";

type AppRole = "super_admin" | "admin" | "editor" | "viewer" | "support_manager";

const ROLE_LABELS: Record<AppRole, string> = {
  super_admin: "Super Admin",
  admin: "Administrator",
  editor: "Editor",
  viewer: "Viewer",
  support_manager: "Support Manager",
};

const ROLE_COLORS: Record<AppRole, string> = {
  super_admin: "bg-primary/15 text-primary border-primary/30",
  admin: "bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400",
  editor: "bg-purple-500/15 text-purple-600 border-purple-500/30 dark:text-purple-400",
  viewer: "bg-muted text-muted-foreground border-border",
  support_manager: "bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400",
};

interface MemberRow {
  user_id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  roles: AppRole[];
  status: string;
  created_at: string;
  last_login_at: string | null;
  login_method: string | null;
  created_by: string | null;
}

interface InvitationRow {
  id: string;
  email: string;
  role: AppRole;
  token: string;
  status: string;
  expires_at: string;
  created_at: string;
}

const TeamMembers = () => {
  const { toast } = useToast();
  const { isSuperAdmin, userId: currentUserId, loading: permLoading } = usePermissions();
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [invitations, setInvitations] = useState<InvitationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");

  // Invite dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<AppRole>("editor");
  const [inviting, setInviting] = useState(false);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);

  // Confirm dialogs
  const [confirmAction, setConfirmAction] = useState<{
    type: "suspend" | "activate" | "remove" | "revoke" | "reset_password" | "force_logout" | "disable_login" | "enable_login" | "resend_credentials";
    id: string;
    label: string;
  } | null>(null);

  const load = async () => {
    setLoading(true);
    // Roles
    const { data: roleRows } = await supabase
      .from("user_roles")
      .select("user_id, role");
    const byUser = new Map<string, AppRole[]>();
    (roleRows ?? []).forEach((r: any) => {
      const arr = byUser.get(r.user_id) ?? [];
      arr.push(r.role);
      byUser.set(r.user_id, arr);
    });
    const userIds = Array.from(byUser.keys());

    // Profiles (now includes login_method, last_login_at, created_by)
    const { data: profileRows } = userIds.length
      ? await supabase.from("profiles").select("id, full_name, avatar_url, login_method, last_login_at, created_by").in("id", userIds)
      : { data: [] as any[] };
    const profileMap = new Map<string, any>((profileRows ?? []).map((p: any) => [p.id, p]));

    // Status
    const { data: statusRows } = userIds.length
      ? await supabase.from("team_member_status" as any).select("user_id, status").in("user_id", userIds)
      : { data: [] as any[] };
    const statusMap = new Map<string, string>((statusRows ?? []).map((s: any) => [s.user_id, s.status]));

    // Last login per user from activity log
    const { data: loginRows } = userIds.length
      ? await supabase
          .from("admin_login_activity" as any)
          .select("user_id, logged_in_at, login_method")
          .in("user_id", userIds)
          .order("logged_in_at", { ascending: false })
      : { data: [] as any[] };
    const lastLoginMap = new Map<string, string>();
    const loginMethodMap = new Map<string, string>();
    (loginRows ?? []).forEach((r: any) => {
      if (!lastLoginMap.has(r.user_id)) {
        lastLoginMap.set(r.user_id, r.logged_in_at);
        if (r.login_method) loginMethodMap.set(r.user_id, r.login_method);
      }
    });

    const { data: invs } = await supabase
      .from("team_invitations" as any)
      .select("email, accepted_by, status, role, token, id, expires_at, created_at")
      .order("created_at", { ascending: false });
    const emailMap = new Map<string, string>();
    (invs ?? []).forEach((i: any) => {
      if (i.accepted_by) emailMap.set(i.accepted_by, i.email);
    });

    const rows: MemberRow[] = userIds.map((uid) => {
      const p = profileMap.get(uid);
      return {
        user_id: uid,
        email: emailMap.get(uid) ?? (uid === currentUserId ? "you" : "—"),
        full_name: p?.full_name ?? null,
        avatar_url: p?.avatar_url ?? null,
        roles: byUser.get(uid) ?? [],
        status: statusMap.get(uid) ?? "active",
        created_at: "",
        last_login_at: lastLoginMap.get(uid) ?? p?.last_login_at ?? null,
        login_method: loginMethodMap.get(uid) ?? p?.login_method ?? null,
        created_by: p?.created_by ?? null,
      };
    });

    setMembers(rows);
    setInvitations(((invs ?? []) as any[]).filter((i) => i.status === "pending"));
    setLoading(false);
  };

  useEffect(() => {
    if (!permLoading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permLoading]);

  const filtered = members.filter((m) => {
    if (roleFilter !== "all" && !m.roles.includes(roleFilter as AppRole)) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!m.email.toLowerCase().includes(q) && !(m.full_name ?? "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const handleInvite = async () => {
    if (!inviteEmail.includes("@")) {
      toast({ variant: "destructive", title: "Invalid email" });
      return;
    }
    setInviting(true);
    const { data, error } = await supabase
      .from("team_invitations" as any)
      .insert({ email: inviteEmail.trim().toLowerCase(), role: inviteRole, invited_by: currentUserId })
      .select()
      .single();
    setInviting(false);
    if (error) {
      toast({ variant: "destructive", title: "Failed to create invitation", description: error.message });
      return;
    }
    const link = `${window.location.origin}/accept-invite?token=${(data as any).token}`;
    await navigator.clipboard.writeText(link).catch(() => {});
    toast({
      title: "Invitation created",
      description: "Invite link copied to clipboard. Share it with the new member.",
    });
    setInviteEmail("");
    setInviteOpen(false);
    load();
  };

  const handleChangeRole = async (uid: string, role: AppRole) => {
    const { error } = await supabase.rpc("assign_user_role" as any, {
      _target_user: uid, _role: role,
    });
    if (error) {
      toast({ variant: "destructive", title: "Failed", description: error.message });
      return;
    }
    toast({ title: "Role updated" });
    load();
  };

  const handleStatus = async (uid: string, status: "active" | "suspended") => {
    const { error } = await supabase
      .from("team_member_status" as any)
      .upsert({ user_id: uid, status, suspended_at: status === "suspended" ? new Date().toISOString() : null });
    if (error) {
      toast({ variant: "destructive", title: "Failed", description: error.message });
      return;
    }
    toast({ title: status === "suspended" ? "Member suspended" : "Member reactivated" });
    load();
  };

  const handleRemove = async (uid: string) => {
    const { error } = await supabase.from("user_roles").delete().eq("user_id", uid);
    if (error) {
      toast({ variant: "destructive", title: "Failed", description: error.message });
      return;
    }
    await supabase.from("team_member_status" as any).delete().eq("user_id", uid);
    toast({ title: "Team member removed" });
    load();
  };

  const handleRevokeInvite = async (id: string) => {
    await supabase.from("team_invitations" as any).update({ status: "revoked" }).eq("id", id);
    toast({ title: "Invitation revoked" });
    load();
  };

  const copyInviteLink = (token: string) => {
    const link = `${window.location.origin}/accept-invite?token=${token}`;
    navigator.clipboard.writeText(link);
    toast({ title: "Invite link copied" });
  };

  const callTeamAction = async (action: string, uid: string) => {
    const { data, error } = await supabase.functions.invoke("admin-team-action", {
      body: { action, user_id: uid, login_url: `${window.location.origin}/admin/login` },
    });
    if (error || (data as any)?.error) {
      toast({ variant: "destructive", title: "Action failed", description: (data as any)?.error ?? error?.message });
      return;
    }
    if (action === "reset_password" || action === "resend_credentials") {
      const tp = (data as any).temp_password as string;
      const em = (data as any).email as string;
      await navigator.clipboard.writeText(`Email: ${em}\nPassword: ${tp}`).catch(() => {});
      toast({
        title: (data as any).email_sent ? "Password reset & emailed" : "Password reset",
        description: `New temporary password copied to clipboard: ${tp}`,
      });
    } else {
      toast({ title: "Done" });
    }
    load();
  };

  if (permLoading) {
    return <AdminLayout><div className="p-8">Loading...</div></AdminLayout>;
  }

  if (!isSuperAdmin) {
    return (
      <AdminLayout>
        <div className="max-w-2xl mx-auto mt-12">
          <Card>
            <CardHeader>
              <CardTitle>Restricted</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Only Super Admins can manage team members.
              </p>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Team Management</h1>
            <p className="text-muted-foreground mt-1">Invite team members and assign roles.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => setCreateOpen(true)}>
              <UserCog className="h-4 w-4 mr-2" /> Create Team Member
            </Button>
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
              <DialogTrigger asChild>
                <Button><UserPlus className="h-4 w-4 mr-2" />Invite Member</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite Team Member</DialogTitle>
                </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label htmlFor="invite-email">Email</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="teammate@example.com"
                  />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as AppRole)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrator</SelectItem>
                      <SelectItem value="editor">Editor</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                      <SelectItem value="support_manager">Support Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-sm text-muted-foreground">
                  An invite link will be generated and copied to your clipboard. Share it with the new member — they'll need to sign in (or sign up) with this email to accept.
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
                <Button onClick={handleInvite} disabled={inviting}>
                  {inviting ? "Creating..." : "Create Invite"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="sm:w-[200px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
                <SelectItem value="admin">Administrator</SelectItem>
                <SelectItem value="editor">Editor</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
                <SelectItem value="support_manager">Support Manager</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Members table */}
        <Card>
          <CardHeader><CardTitle>Members ({filtered.length})</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-muted-foreground">Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">No team members found.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Member</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Login Method</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Login</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((m) => (
                      <TableRow key={m.user_id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                              {m.avatar_url ? (
                                <img src={m.avatar_url} alt="" className="h-full w-full object-cover" />
                              ) : (
                                <span className="text-sm font-medium">
                                  {(m.full_name ?? m.email).charAt(0).toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div>
                              <div className="font-medium">{m.full_name ?? "—"}</div>
                              <div className="text-xs text-muted-foreground">{m.email}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {m.roles.map((r) => (
                              <Badge key={r} variant="outline" className={ROLE_COLORS[r]}>
                                {ROLE_LABELS[r]}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {m.login_method ?? "—"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            m.status === "active"
                              ? "bg-green-500/15 text-green-600 border-green-500/30 dark:text-green-400"
                              : m.status === "suspended"
                              ? "bg-destructive/15 text-destructive border-destructive/30"
                              : "bg-amber-500/15 text-amber-600 border-amber-500/30"
                          }>
                            {m.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {m.last_login_at
                            ? formatDistanceToNow(new Date(m.last_login_at), { addSuffix: true })
                            : "Never"}
                        </TableCell>
                        <TableCell className="text-right">
                          {m.user_id === currentUserId ? (
                            <span className="text-xs text-muted-foreground">You</span>
                          ) : m.roles.includes("super_admin") ? (
                            <span className="text-xs text-muted-foreground">Protected</span>
                          ) : (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleChangeRole(m.user_id, "admin")}>Set Administrator</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleChangeRole(m.user_id, "editor")}>Set Editor</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleChangeRole(m.user_id, "viewer")}>Set Viewer</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleChangeRole(m.user_id, "support_manager")}>Set Support Manager</DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => callTeamAction("reset_password", m.user_id)}>Reset Password</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => callTeamAction("resend_credentials", m.user_id)}>Resend Credentials</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => callTeamAction("force_logout", m.user_id)}>Force Logout</DropdownMenuItem>
                                {m.status === "suspended" ? (
                                  <DropdownMenuItem onClick={() => callTeamAction("enable_login", m.user_id)}>
                                    Enable Login
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => callTeamAction("disable_login", m.user_id)}>
                                    Disable Login
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                {m.status === "suspended" ? (
                                  <DropdownMenuItem onClick={() => setConfirmAction({ type: "activate", id: m.user_id, label: m.full_name ?? m.email })}>
                                    Reactivate Access
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => setConfirmAction({ type: "suspend", id: m.user_id, label: m.full_name ?? m.email })}>
                                    Suspend Access
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => setConfirmAction({ type: "remove", id: m.user_id, label: m.full_name ?? m.email })}
                                >
                                  Remove Member
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending invitations */}
        <Card>
          <CardHeader><CardTitle>Pending Invitations ({invitations.length})</CardTitle></CardHeader>
          <CardContent>
            {invitations.length === 0 ? (
              <p className="text-sm text-muted-foreground">No pending invitations.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invitations.map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" /> {inv.email}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={ROLE_COLORS[inv.role]}>{ROLE_LABELS[inv.role]}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDistanceToNow(new Date(inv.expires_at), { addSuffix: true })}
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button variant="outline" size="sm" onClick={() => copyInviteLink(inv.token)}>
                            <Copy className="h-3 w-3 mr-1" /> Copy Link
                          </Button>
                          <Button
                            variant="outline" size="sm"
                            onClick={() => setConfirmAction({ type: "revoke", id: inv.id, label: inv.email })}
                          >
                            Revoke
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirmation dialog */}
      <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === "remove" && `Remove ${confirmAction.label}?`}
              {confirmAction?.type === "suspend" && `Suspend ${confirmAction.label}?`}
              {confirmAction?.type === "activate" && `Reactivate ${confirmAction.label}?`}
              {confirmAction?.type === "revoke" && `Revoke invitation for ${confirmAction.label}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === "remove" && "All roles and access will be removed. This cannot be undone."}
              {confirmAction?.type === "suspend" && "The member will lose dashboard access until reactivated."}
              {confirmAction?.type === "activate" && "The member will regain dashboard access."}
              {confirmAction?.type === "revoke" && "The invite link will stop working immediately."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!confirmAction) return;
                if (confirmAction.type === "remove") handleRemove(confirmAction.id);
                if (confirmAction.type === "suspend") handleStatus(confirmAction.id, "suspended");
                if (confirmAction.type === "activate") handleStatus(confirmAction.id, "active");
                if (confirmAction.type === "revoke") handleRevokeInvite(confirmAction.id);
                setConfirmAction(null);
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CreateTeamMemberDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />
    </AdminLayout>
  );
};

export default TeamMembers;
